import "./index.css";

import { useCallback, useEffect, useState } from "react";

import { Shell } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Link, useRoute } from "@/lib/router";
import { Admin } from "@/pages/Admin";
import { Join } from "@/pages/Join";
import { Landing } from "@/pages/Landing";
import { Match } from "@/pages/Match";
import { Matches } from "@/pages/Matches";
import { Profile } from "@/pages/Profile";
import { PublicProfile } from "@/pages/PublicProfile";
import type { Profile as ProfileT } from "@/shared/types";

const ROUTES = [
  "/",
  "/join",
  "/profile",
  "/matches",
  "/admin",
  "/u/:slug",
  "/match/:id/:token",
] as const;

function NotFound() {
  return (
    <Shell>
      <div className="flex min-h-[65vh] flex-col justify-center py-16">
        <h1 className="text-5xl font-semibold tracking-[-0.03em]">Nothing here.</h1>
        <p className="mt-4 max-w-md text-[15.5px] leading-relaxed text-muted-foreground">
          There isn't much to this site by design, so there aren't many pages to get lost on.
        </p>
        <div className="mt-8">
          <Button asChild className="rounded-full px-6">
            <Link href="/">Back to the start</Link>
          </Button>
        </div>
      </div>
    </Shell>
  );
}

export function App() {
  const { pattern, params } = useRoute(ROUTES);
  const [profile, setProfile] = useState<ProfileT | null>(null);

  // One "who am I" call for the whole app; pages needing more fetch their own.
  useEffect(() => {
    api
      .get<{ profile: ProfileT | null }>("/api/me")
      .then((b) => setProfile(b.profile))
      .catch(() => setProfile(null));
  }, []);

  const onProfileChange = useCallback((p: ProfileT) => setProfile(p), []);

  switch (pattern) {
    case "/":
      return <Landing profile={profile} />;
    case "/join":
      return <Join />;
    case "/profile":
      return <Profile onProfileChange={onProfileChange} />;
    case "/matches":
      return <Matches />;
    case "/admin":
      return <Admin />;
    case "/u/:slug":
      return <PublicProfile slug={params.slug!} />;
    case "/match/:id/:token":
      return <Match id={params.id!} token={params.token!} />;
    default:
      return <NotFound />;
  }
}

export default App;
